import { Input } from "./ui/input";
import { useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Printer, Wallet, TrendingDown, ShoppingCart, AlertTriangle, Fuel, Award } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { SAR_SVG } from "../lib/custodyReceipt";
import { Money } from "./SaudiRiyal";

const money = (n) => `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} ${SAR_SVG}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);

const Section = ({ icon: Icon, title, total, tone = "slate", children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid={`stmt-section-${title}`}>
    <div className="flex items-center justify-between gap-2 mb-1.5">
      <p className="font-semibold text-slate-700 text-sm flex items-center gap-1.5"><Icon className={`h-4 w-4 text-${tone}-500`} /> {title}</p>
      {total != null && <span className={`font-mono-nums font-bold text-${tone}-700 text-sm`}><Money value={total} decimals /></span>}
    </div>
    {children}
  </div>
);

export const StatementDialog = ({ open, onOpenChange, employees = [] }) => {
  const [empId, setEmpId] = useState("");
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!empId) { toast.warning("اختر الموظف"); return; }
    setBusy(true); setData(null);
    try { setData(await api.employeeStatement(empId, month)); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر تحميل الكشف"); }
    finally { setBusy(false); }
  };

  const printStatement = () => {
    if (!data) return;
    const d = data;
    const row = (l, v, cls = "") => `<tr><td>${l}</td><td class="num ${cls}">${money(v)}</td></tr>`;
    const html = `<html dir="rtl"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"><title>كشف حساب ${d.employee.name} - ${d.month}</title>
      <style>body{font-family:SaudiRiyal,'Segoe UI',Tahoma,sans-serif;padding:24px;color:#1e293b}h1{font-size:20px;margin:0}
      .head{display:flex;justify-content:space-between;border-bottom:2px solid #2563eb;padding-bottom:8px;margin-bottom:12px}
      .muted{color:#64748b;font-size:13px}table{width:100%;border-collapse:collapse;margin:8px 0}
      td{padding:6px 8px;border-bottom:1px solid #eef2f7;font-size:13px}.num{text-align:left;font-variant-numeric:tabular-nums}
      h3{font-size:14px;margin:14px 0 4px;color:#334155}.net{background:#ecfdf5;border:1px solid #a7f3d0;border-radius:10px;padding:10px;display:flex;justify-content:space-between;font-weight:bold;font-size:16px;margin-top:14px}
      .red{color:#dc2626}.green{color:#059669}</style></head><body>
      <div class="head"><div><h1>كشف حساب المندوب</h1><div class="muted">${d.org_name || ""}</div></div>
      <div class="muted">الشهر: ${d.month}<br>${d.employee.name}${d.employee.rider_id ? " · " + d.employee.rider_id : ""}<br>${d.employee.branch || ""}</div></div>
      <h3>الراتب والبدلات</h3><table>${(d.salary.lines || []).map((l) => row(l.label, l.value, l.group === "deduct" ? "red" : "")).join("") || row("إجمالي الراتب", d.salary.gross)}<tr><td><b>صافي الراتب</b></td><td class="num green"><b>${money(d.salary.net)}</b></td></tr></table>
      <h3>الخصومات على المندوب</h3><table>${row("السُّلف", d.advances.total, "red")}${row("المشتريات المعتمدة", d.purchases.total, "red")}${row("خصومات المخالفات", d.violations.total, "red")}</table>
      <h3>معلومات إضافية</h3><table>${row("الوقود (" + d.fuel.count + " عملية)", d.fuel.total)}<tr><td>نقاط الولاء</td><td class="num">+${d.loyalty.earned} / -${d.loyalty.redeemed}</td></tr></table>
      <div class="net"><span>الصافي المستحق</span><span>${money(d.net_payable)}</span></div>
      <p class="muted" style="margin-top:20px">تم الإصدار بواسطة WorkZone</p></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("فعّل النوافذ المنبثقة للطباعة"); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { w.print(); }, 400);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg font-body max-h-[92vh] overflow-y-auto" dir="rtl" data-testid="statement-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-lg flex items-center gap-2"><FileText className="h-5 w-5 text-blue-600" /> كشف حساب المندوب</DialogTitle>
          <DialogDescription className="text-right">اختر الموظف والشهر لعرض كشف حساب موحّد قابل للطباعة/التصدير PDF.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end gap-2">
          <label className="flex-1 min-w-[160px]"><span className="text-[11px] text-slate-500">الموظف</span>
            <select data-testid="stmt-employee" value={empId} onChange={(e) => setEmpId(e.target.value)}
              className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
              <option value="">— اختر —</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </label>
          <label><span className="text-[11px] text-slate-500">الشهر</span>
            <Input data-testid="stmt-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="mt-1 h-9 rounded-md border border-slate-200 bg-white px-2 text-sm font-mono-nums" dir="ltr" />
          </label>
          <Button data-testid="stmt-load" onClick={load} disabled={busy} className="h-9 bg-blue-600 hover:bg-blue-700">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "عرض"}
          </Button>
        </div>

        {data && (
          <div className="mt-3 space-y-2" data-testid="statement-body">
            <Section icon={Wallet} title="صافي الراتب" total={data.salary.net} tone="emerald">
              {!data.salary.found && <p className="text-xs text-amber-600">لا يوجد راتب مسجّل لهذا الشهر.</p>}
              {data.salary.lines.length > 0 && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                  {data.salary.lines.map((l, i) => (
                    <div key={i} className="flex justify-between text-xs border-b border-slate-50 py-0.5">
                      <span className={l.group === "deduct" ? "text-rose-500" : "text-slate-500"}>{l.label}</span>
                      <span className="font-mono-nums text-slate-700">{money(l.value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Section>
            <Section icon={TrendingDown} title="السُّلف" total={data.advances.total} tone="rose" />
            <Section icon={ShoppingCart} title="المشتريات المعتمدة" total={data.purchases.total} tone="rose" />
            <Section icon={AlertTriangle} title="خصومات المخالفات" total={data.violations.total} tone="rose" />
            <Section icon={Fuel} title="الوقود" total={data.fuel.total} tone="slate">
              <p className="text-[11px] text-slate-400">{data.fuel.count} عملية · {data.fuel.liters} لتر</p>
            </Section>
            <Section icon={Award} title="نقاط الولاء" tone="amber">
              <p className="text-xs text-slate-500">مكتسبة <b className="text-emerald-600 font-mono-nums">+{data.loyalty.earned}</b> · مستبدلة <b className="text-rose-600 font-mono-nums">-{data.loyalty.redeemed}</b></p>
            </Section>
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3 flex items-center justify-between" data-testid="stmt-net-payable">
              <span className="font-bold text-emerald-800">الصافي المستحق</span>
              <span className="font-mono-nums font-extrabold text-emerald-700 text-lg">{money(data.net_payable)}</span>
            </div>
            <p className="text-[10px] text-slate-400 text-center">الصافي المستحق = صافي الراتب − (السُّلف + المشتريات + خصومات المخالفات)</p>
            <Button data-testid="stmt-print" onClick={printStatement} className="w-full gap-1.5 bg-slate-800 hover:bg-slate-900">
              <Printer className="h-4 w-4" /> طباعة / تصدير PDF
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
